import type { ReactNode } from 'react';
import type { z } from 'zod';

import { useParsedQueryParams } from '../../hooks/useParsedQueryParams';
import NoDataDashboard from '../NoDataDashboard';

type BaseRecord = Record<string, string>;

interface RequiredKeysProps<T extends BaseRecord> {
  requiredKeys: (keyof T)[];
  schema?: never;
  notValidMessage?: string;
  children: ReactNode | ((params: T) => ReactNode);
}

interface SchemaProps<S extends z.ZodType> {
  schema: S;
  requiredKeys?: never;
  notValidMessage?: string;
  children: ReactNode | ((params: z.infer<S>) => ReactNode);
}

export function QueryResolutionGuard<T extends BaseRecord>(props: RequiredKeysProps<T>): ReactNode;
export function QueryResolutionGuard<S extends z.ZodType>(props: SchemaProps<S>): ReactNode;
export function QueryResolutionGuard<T extends BaseRecord, S extends z.ZodType>(
  props: RequiredKeysProps<T> | SchemaProps<S>,
): ReactNode {
  const { notValidMessage = 'Required parameters are missing', children } = props;
  const params = useParsedQueryParams<T>();

  // If a zod schema is provided, validate against the schema
  if (props.schema) {
    const result = props.schema.safeParse(params);

    if (!result.success) {
      const customIssue = result.error.issues.find((issue) => issue.code === 'custom');
      const issue = customIssue ?? result.error.issues[0];

      let errorMessage = notValidMessage;
      if (issue) {
        errorMessage = issue.code === 'custom' ? issue.message : `Missing required parameter: ${issue.path.join('.')}`;
      }

      return <NoDataDashboard message={errorMessage} />;
    }

    const typedChildren = children as ReactNode | ((params: z.infer<S>) => ReactNode);
    return <>{typeof typedChildren === 'function' ? typedChildren(result.data) : typedChildren}</>;
  }

  // If required keys are provided, validate that they are present in the query params
  if (props.requiredKeys) {
    const isValid = props.requiredKeys.every((key) => !!params[key]);

    if (!isValid) {
      return <NoDataDashboard message={notValidMessage} />;
    }

    const typedChildren = children as ReactNode | ((params: T) => ReactNode);
    return <>{typeof typedChildren === 'function' ? typedChildren(params) : typedChildren}</>;
  }

  return <>{children}</>;
}
